<script>
import { Bar } from 'vue-chartjs'
import Chart from 'chart.js'

export default {
  name: "BarChartComponent",
  extends: Bar,
  props: {
    foodProfiles: {
      type: Object,
      required: true
    },
    foodName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      chartData: {
        labels: [],
        datasets: []
      },
      options: {
        animation: {
          duration: 700,
          easing: 'easeOutQuart'
        },
        responsiveAnimationDuration: 350,
        hover: {
          animationDuration: 200,
          mode: 'index',
          intersect: false
        },
        tooltips: {
          callbacks: {
            title: function(tooltipItem, data) {
              const date = data.labels[tooltipItem[0].index]
              const value = (tooltipItem[0].yLabel / 1000).toFixed(2)
              
              // Formatear la fecha
              const dateObj = new Date(date)
              const formattedDate = dateObj.toLocaleString('en-US', {
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              })

              return [
                `Fecha: ${formattedDate}`,
                `Materia Prima: ${this.foodName}`,
                `Peso: ${value} ton`
              ]
            }.bind(this),
            label: function() {
              return null // No queremos el label por defecto
            }
          },
          backgroundColor: '#fff',
          titleFontSize: 14,
          titleFontColor: '#89c25b',
          bodyFontColor: '#000',
          bodyFontSize: 14,
          displayColors: false,
          borderColor: 'rgba(137, 194, 91, 1)',
          borderWidth: 2
        },
        legend: {
          display: true,
          position: 'top'
        },
        scales: {
          xAxes: [{
            gridLines: {
              color: "rgba(120, 184, 67, .125)",
              display: true,
            },
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'MMM DD HH:mm'
              }
            },
            ticks: {
              callback: function(value) {
                const date = new Date(value);
                return date.toLocaleString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                });
              }
            }
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              callback: function(value) {
                return (value / 1000).toFixed(2) + ' ton'
              }
            }
          }]
        },
        layout: {
          padding: {
            left: 0
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          verticalLine: {
            lineColor: 'rgba(128, 0, 128, 0.5)',
            lineWidth: 2,
            lineDash: [5, 5]
          }
        }
      }
    }
  },
  watch: {
    foodProfiles: {
      handler(newValue) {
        this.updateChartData(newValue)
      },
      deep: true
    }
  },
  methods: {
    updateChartData(profiles) {
      const datasets = []
      const allDates = new Set()

      // Collect all unique dates
      Object.values(profiles).forEach(foodData => {
        foodData.forEach(item => {
          allDates.add(item.date)
        })
      })

      // Sort dates
      const sortedDates = Array.from(allDates).sort()

      // Create dataset for each food type
      // eslint-disable-next-line no-unused-vars
      Object.entries(profiles).forEach(([foodType, foodData], index) => {
        const colors = [
          { bg: 'rgba(137, 194, 91, 0.5)', border: 'rgba(137, 194, 91, 1)' },
          { bg: 'rgba(255, 99, 132, 0.5)', border: 'rgba(255, 99, 132, 1)' },
          { bg: 'rgba(75, 192, 192, 0.5)', border: 'rgba(75, 192, 192, 1)' }
        ]

        const colorIndex = index % colors.length
        const data = sortedDates.map(date => {
          const dayData = foodData.find(item => item.date === date)
          return dayData ? dayData.totalLevel : 0
        })

        datasets.push({
          label: this.foodName,
          data: data,
          backgroundColor: colors[colorIndex].bg,
          borderColor: colors[colorIndex].border,
          borderWidth: 1
        })
      })

      this.chartData = {
        labels: sortedDates,
        datasets: datasets
      }

      // Registrar el plugin de línea vertical
      this.registerVerticalLinePlugin()
      if (this.$data._chart) {
        this.$data._chart.data.labels = this.chartData.labels
        this.$data._chart.data.datasets = this.chartData.datasets
        this.$data._chart.update()
      } else {
        this.renderChart(this.chartData, this.options)
      }
    },
    registerVerticalLinePlugin() {
      const verticalLinePlugin = {
        id: 'verticalLine',
        afterDraw: function(chart) {
          if (chart.tooltip._active && chart.tooltip._active.length) {
            const activePoint = chart.tooltip._active[0]
            const ctx = chart.ctx
            const x = activePoint.tooltipPosition().x
            const topY = chart.scales['y-axis-0'].top
            const bottomY = chart.scales['y-axis-0'].bottom

            // Guardar el estado actual
            ctx.save()
            
            // Configurar el estilo de la línea
            ctx.beginPath()
            ctx.moveTo(x, topY)
            ctx.lineTo(x, bottomY)
            ctx.lineWidth = 2
            ctx.strokeStyle = 'rgba(128, 0, 128, 0.5)'
            ctx.setLineDash([5, 5])
            ctx.stroke()
            
            // Restaurar el estado
            ctx.restore()
          }
        }
      }

      // Registrar el plugin si no está registrado
      if (!Chart.plugins.getAll().find(p => p.id === 'verticalLine')) {
        Chart.plugins.register(verticalLinePlugin)
      }
    }
  },
  mounted() {
    this.updateChartData(this.foodProfiles)
  }
}
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 400px;
  width: 100%;
}
</style>
